import React, { useState, useEffect, useMemo } from 'react';
import { Box, Text, useInput, useApp } from 'ink';
import Spinner from 'ink-spinner';
import TextInput from 'ink-text-input';
import type { Jai1Config } from '../../types/framework.types.js';
import { ComponentsService, type Component, type InstalledComponent, type Tag } from '../../services/components.service.js';
import { MigrateIdeService } from '../../services/migrate-ide.service.js';
import { IDE_MIGRATION_CONFIGS, getMigrationIDEs } from '../../constants/ide-migration-configs.js';
import type { MigrateIDE, MigrateContentType } from '../../types/migrate-ide.types.js';
import { ProgressBar } from '../shared/ProgressBar.js';
import { StatusIcon } from '../shared/StatusIcon.js';
import { ErrorLogService } from '../../services/error-log.service.js';
import { assertPrimaryPackageCompatibility } from '../../services/primary-package-policy.service.js';
import {
    buildPackageOptions,
    getNoResultsSuggestion,
    getRecommendedPackageOptions,
    getSelectedPackageTags,
    getVisibleWindow,
    searchCatalogComponents,
    type CatalogPackageOption,
    type CatalogSearchResult,
} from './catalog.js';

type ViewState = 'browse' | 'previewing' | 'preview' | 'installing' | 'summary' | 'ide-sync' | 'syncing' | 'done';

interface UnifiedApplyAppProps {
    config: Jai1Config;
    force?: boolean;
    onExit: () => void;
}

interface InstallProgress {
    filepath: string;
    status: 'pending' | 'downloading' | 'success' | 'error';
    error?: string;
}

interface SyncProgress {
    targetPath: string;
    status: 'pending' | 'syncing' | 'success' | 'error';
    error?: string;
}

interface PreviewItem {
    filepath: string;
    status: 'add' | 'update' | 'skip' | 'blocked';
    isDependency: boolean;
    reason: string;
}

interface PreviewStats {
    total: number;
    add: number;
    update: number;
    skip: number;
    blocked: number;
    dependencies: number;
}

const PACKAGE_WINDOW_SIZE = 8;
const RESULT_WINDOW_SIZE = 7;

/**
 * Unified Apply App - All-in-one interface
 */
export const UnifiedApplyApp: React.FC<UnifiedApplyAppProps> = ({
    config,
    force = false,
    onExit
}) => {
    const { exit } = useApp();

    // View state
    const [viewState, setViewState] = useState<ViewState>('browse');

    // Data state
    const [components, setComponents] = useState<Component[]>([]);
    const [tags, setTags] = useState<Tag[]>([]);
    const [installed, setInstalled] = useState<Record<string, InstalledComponent>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // UI state - catalog layout: package pane + searchable component pane
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPaths, setSelectedPaths] = useState<Set<string>>(new Set());
    const [cursorIndex, setCursorIndex] = useState(0);
    const [activePane, setActivePane] = useState<'packages' | 'components'>('components');
    const [isSearchFocused, setIsSearchFocused] = useState(true);
    const [selectedPackageIndex, setSelectedPackageIndex] = useState(0); // 0 = "All", 1+ = specific package
    const [previewItems, setPreviewItems] = useState<PreviewItem[]>([]);
    const [previewStats, setPreviewStats] = useState<PreviewStats>({ total: 0, add: 0, update: 0, skip: 0, blocked: 0, dependencies: 0 });
    const [previewError, setPreviewError] = useState<string | null>(null);

    // Install state
    const [installProgress, setInstallProgress] = useState<InstallProgress[]>([]);
    const [installStats, setInstallStats] = useState({ total: 0, completed: 0, added: 0, updated: 0, skipped: 0, failed: 0 });

    // IDE sync state
    const [availableIdes, setAvailableIdes] = useState<MigrateIDE[]>([]);
    const [selectedIdes, setSelectedIdes] = useState<Set<MigrateIDE>>(new Set());
    const [ideCursorIndex, setIdeCursorIndex] = useState(0);
    const [syncProgress, setSyncProgress] = useState<SyncProgress[]>([]);
    const [syncStats, setSyncStats] = useState({ total: 0, completed: 0, created: 0, updated: 0, skipped: 0, errors: 0 });

    const service = new ComponentsService();

    // Load data on mount
    useEffect(() => {
        const loadData = async () => {
            try {
                const [comps, tagList, installed] = await Promise.all([
                    service.list(config),
                    service.listTags(config),
                    service.getInstalled()
                ]);
                setComponents(comps);
                setTags(tagList);
                setInstalled(installed);
                // Start with "All" packages (index 0)
                setSelectedPackageIndex(0);
                setLoading(false);
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to load';
                setError(message);
                setLoading(false);

                void new ErrorLogService().logError(err, {
                    command: 'apply',
                    cwdBase: process.cwd().split('/').filter(Boolean).pop(),
                    details: { phase: 'loadData' },
                });
            }
        };
        loadData();

        // Initialize available IDEs
        setAvailableIdes(getMigrationIDEs());
    }, []);

    const packageOptions = useMemo(() => buildPackageOptions(tags, components), [tags, components]);
    const recommendedPackages = useMemo(() => getRecommendedPackageOptions(packageOptions), [packageOptions]);
    const selectedPackage = packageOptions[selectedPackageIndex] || packageOptions[0];
    const searchResults = useMemo(
        () => searchCatalogComponents(components, tags, {
            query: searchQuery,
            packageTag: selectedPackage?.tag,
        }),
        [components, tags, searchQuery, selectedPackage?.tag],
    );
    const filteredComponents = useMemo(() => searchResults.map((result) => result.component), [searchResults]);

    useEffect(() => {
        setCursorIndex(0);
    }, [searchQuery, selectedPackageIndex]);

    useEffect(() => {
        if (cursorIndex >= searchResults.length) {
            setCursorIndex(Math.max(0, searchResults.length - 1));
        }
    }, [cursorIndex, searchResults.length]);

    // Handle keyboard input
    useInput((input, key) => {
        if (viewState === 'done') {
            if (key.return || input === 'q' || key.escape) {
                onExit();
            }
            return;
        }

        if (viewState === 'summary') {
            if (key.return) {
                setViewState('ide-sync');
            } else if (input === 's' || (input === 'q' && key.ctrl) || key.escape) {
                onExit();
            }
            return;
        }

        if (viewState === 'ide-sync') {
            if (key.upArrow) {
                setIdeCursorIndex(prev => Math.max(0, prev - 1));
            } else if (key.downArrow) {
                setIdeCursorIndex(prev => Math.min(availableIdes.length - 1, prev + 1));
            } else if (input === ' ') {
                // Toggle IDE selection
                const ide = availableIdes[ideCursorIndex];
                if (ide) {
                    setSelectedIdes(prev => {
                        const next = new Set(prev);
                        if (next.has(ide)) {
                            next.delete(ide);
                        } else {
                            next.add(ide);
                        }
                        return next;
                    });
                }
            } else if (input === 'a') {
                setSelectedIdes(new Set(availableIdes));
            } else if (input === 'c') {
                setSelectedIdes(new Set());
            } else if (key.return) {
                if (selectedIdes.size > 0) {
                    handleIdeSync();
                }
            } else if (input === 's' || (input === 'q' && key.ctrl) || key.escape) {
                onExit();
            }
            return;
        }

        if (viewState === 'syncing' || viewState === 'installing' || viewState === 'previewing') {
            return;
        }

        if (viewState === 'preview') {
            if (previewError) {
                if (key.escape) {
                    setViewState('browse');
                } else if (input === 'q' && key.ctrl) {
                    onExit();
                }
                return;
            }

            if (key.escape) {
                setViewState('browse');
            } else if (key.return) {
                handleApply(false);
            } else if (input === 's') {
                handleApply(true);
            } else if (input === 'q' && key.ctrl) {
                onExit();
            }
            return;
        }

        if (isSearchFocused) {
            if (input === 'q' && key.ctrl) {
                onExit();
            } else if (key.escape || key.return || key.downArrow) {
                setIsSearchFocused(false);
                setActivePane('components');
            } else if (key.tab) {
                setIsSearchFocused(false);
                setActivePane('packages');
            }
            return;
        }

        if (input === '/') {
            setIsSearchFocused(true);
            setActivePane('components');
            return;
        }

        if (key.escape || (input === 'q' && key.ctrl)) {
            onExit();
            return;
        }

        if (key.tab) {
            setActivePane(prev => prev === 'packages' ? 'components' : 'packages');
            return;
        }

        if (key.leftArrow) {
            setActivePane('packages');
            return;
        }

        if (key.rightArrow) {
            setActivePane('components');
            return;
        }

        if (key.return) {
            if (selectedPaths.size > 0) {
                handlePreview();
            }
            return;
        }

        if (/^[1-3]$/.test(input)) {
            const recommended = recommendedPackages[Number(input) - 1];
            if (recommended) {
                addPackageToSelection(recommended.tag);
            }
            return;
        }

        if (input === 'a') {
            setSelectedPaths(prev => {
                const next = new Set(prev);
                filteredComponents.forEach(c => next.add(c.filepath));
                return next;
            });
            return;
        }

        if (input === 'c') {
            setSelectedPaths(new Set());
            return;
        }

        if (activePane === 'components') {
            if (key.upArrow) {
                setCursorIndex(prev => Math.max(0, prev - 1));
            } else if (key.downArrow) {
                setCursorIndex(prev => Math.min(filteredComponents.length - 1, prev + 1));
            } else if (input === ' ') {
                const current = filteredComponents[cursorIndex];
                if (current) {
                    setSelectedPaths(prev => {
                        const next = new Set(prev);
                        if (next.has(current.filepath)) {
                            next.delete(current.filepath);
                        } else {
                            next.add(current.filepath);
                        }
                        return next;
                    });
                }
            }
        }

        if (activePane === 'packages') {
            if (key.upArrow) {
                setSelectedPackageIndex(prev => Math.max(0, prev - 1));
            } else if (key.downArrow) {
                setSelectedPackageIndex(prev => Math.min(packageOptions.length - 1, prev + 1));
            } else if (input === ' ') {
                setSelectedPaths(prev => {
                    const next = new Set(prev);
                    filteredComponents.forEach(c => next.add(c.filepath));
                    return next;
                });
            }
        }
    });

    const addPackageToSelection = (tag: string) => {
        const packageIndex = packageOptions.findIndex((option) => option.tag === tag);
        if (packageIndex >= 0) {
            setSelectedPackageIndex(packageIndex);
        }

        const packageComponents = components.filter((component) => component.tags?.includes(tag));
        setSelectedPaths(prev => {
            const next = new Set(prev);
            packageComponents.forEach((component) => next.add(component.filepath));
            return next;
        });
        setActivePane('components');
        setIsSearchFocused(false);
    };

    const handlePreview = async () => {
        setViewState('previewing');
        setPreviewError(null);

        try {
            const pathsToInstall = Array.from(selectedPaths);
            const resolvedPaths = await service.resolveWithDependencies(config, pathsToInstall);
            await assertPrimaryPackageCompatibility({
                projectRoot: process.cwd(),
                requestedPackages: getSelectedPackageTags(resolvedPaths, components, tags),
                requestedPaths: resolvedPaths,
                packageMetadata: tags,
            });
            const installedSnapshot = await service.getInstalled();
            const serverChecksums = await service.getChecksums(config, resolvedPaths);
            const directPaths = new Set(pathsToInstall);
            let add = 0, update = 0, skip = 0, blocked = 0, dependencies = 0;

            const items = resolvedPaths.map((filepath): PreviewItem => {
                const info = installedSnapshot[filepath];
                const isDependency = !directPaths.has(filepath);
                if (isDependency) dependencies++;

                if (info?.modified && !force) {
                    blocked++;
                    return { filepath, status: 'blocked', isDependency, reason: 'modified locally' };
                }

                if (info) {
                    const serverInfo = serverChecksums[filepath];
                    if (serverInfo && info.checksum === serverInfo.checksum && !force) {
                        skip++;
                        return { filepath, status: 'skip', isDependency, reason: 'up-to-date' };
                    }

                    update++;
                    return { filepath, status: 'update', isDependency, reason: force ? 'force overwrite' : 'server changed' };
                }

                add++;
                return { filepath, status: 'add', isDependency, reason: 'new component' };
            });

            setPreviewItems(items);
            setPreviewStats({ total: resolvedPaths.length, add, update, skip, blocked, dependencies });
            setViewState('preview');
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to prepare preview';
            setPreviewError(message);
            setPreviewItems([]);
            setPreviewStats({ total: 0, add: 0, update: 0, skip: 0, blocked: 0, dependencies: 0 });
            setViewState('preview');
            void new ErrorLogService().logError(err, {
                command: 'apply',
                cwdBase: process.cwd().split('/').filter(Boolean).pop(),
                details: { phase: 'preview' },
            });
        }
    };

    // Handle apply
    const handleApply = async (openSyncAfter = false) => {
        const targetDir = process.cwd() + '/.jai1';
        const pathsToInstall = Array.from(selectedPaths);

        // Resolve dependencies
        const resolvedPaths = await service.resolveWithDependencies(config, pathsToInstall);
        await assertPrimaryPackageCompatibility({
            projectRoot: process.cwd(),
            requestedPackages: getSelectedPackageTags(resolvedPaths, components, tags),
            requestedPaths: resolvedPaths,
            packageMetadata: tags,
        });
        setViewState('installing');
        const installedSnapshot = await service.getInstalled();

        // Fetch server checksums for change detection
        const serverChecksums = await service.getChecksums(config, resolvedPaths);

        // Initialize progress
        const initialProgress: InstallProgress[] = resolvedPaths.map(fp => ({
            filepath: fp,
            status: 'pending' as const
        }));
        setInstallProgress(initialProgress);
        setInstallStats({ total: resolvedPaths.length, completed: 0, added: 0, updated: 0, skipped: 0, failed: 0 });

        let added = 0, updated = 0, skipped = 0, failed = 0;
        const failures: Array<{ filepath: string; error: string }> = [];

        for (let i = 0; i < resolvedPaths.length; i++) {
            const fp = resolvedPaths[i]!;
            setInstallProgress(prev => prev.map(p =>
                p.filepath === fp ? { ...p, status: 'downloading' as const } : p
            ));

            try {
                const info = installedSnapshot[fp];
                if (info && !force && info.modified) {
                    const message = 'Modified locally';
                    setInstallProgress(prev => prev.map(p =>
                        p.filepath === fp ? { ...p, status: 'error' as const, error: message } : p
                    ));
                    failures.push({ filepath: fp, error: message });
                    failed++;
                } else if (info && !force) {
                    // Skip if already up to date (checksum match)
                    const serverInfo = serverChecksums[fp];
                    if (serverInfo && info.checksum === serverInfo.checksum) {
                        setInstallProgress(prev => prev.map(p =>
                            p.filepath === fp ? { ...p, status: 'success' as const } : p
                        ));
                        skipped++;
                    } else {
                        await service.install(config, fp, targetDir);
                        setInstallProgress(prev => prev.map(p =>
                            p.filepath === fp ? { ...p, status: 'success' as const } : p
                        ));
                        updated++;
                    }
                } else {
                    await service.install(config, fp, targetDir);
                    setInstallProgress(prev => prev.map(p =>
                        p.filepath === fp ? { ...p, status: 'success' as const } : p
                    ));
                    if (info) updated++;
                    else added++;
                }
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Error';
                setInstallProgress(prev => prev.map(p =>
                    p.filepath === fp ? { ...p, status: 'error' as const, error: message } : p
                ));
                failures.push({ filepath: fp, error: message });
                failed++;
            }

            setInstallStats({ total: resolvedPaths.length, completed: i + 1, added, updated, skipped, failed });
        }

        if (failed > 0) {
            void new ErrorLogService().logError(new Error('apply failed'), {
                command: 'apply',
                cwdBase: process.cwd().split('/').filter(Boolean).pop() || undefined,
                details: { phase: 'install', failures },
            });
        }

        const refreshedInstalled = await service.getInstalled();
        setInstalled(refreshedInstalled);
        setViewState(openSyncAfter ? 'ide-sync' : 'summary');
    };

    // Handle IDE sync
    const handleIdeSync = async () => {
        setViewState('syncing');

        const migrateService = new MigrateIdeService();
        const content = await migrateService.scanJai1Content();

        if (content.totalCount === 0) {
            // No content to sync, go to done
            setSyncStats({ total: 0, completed: 0, created: 0, updated: 0, skipped: 0, errors: 0 });
            setViewState('done');
            return;
        }

        const selectedIdeList = Array.from(selectedIdes);
        const contentTypes: MigrateContentType[] = ['rules', 'workflows', 'skills'];

        // Calculate total files
        const totalItems = contentTypes.reduce((sum, type) => {
            return sum + (type === 'rules' ? content.rules.length :
                type === 'workflows' ? content.workflows.length :
                    type === 'commands' ? content.commands.length :
                        content.skills.length);
        }, 0);
        const totalFiles = totalItems * selectedIdeList.length;

        // Initialize progress
        setSyncStats({ total: totalFiles, completed: 0, created: 0, updated: 0, skipped: 0, errors: 0 });

        let created = 0, updated = 0, skipped = 0, errors = 0;
        let completed = 0;

        const results = await migrateService.migrate(
            selectedIdeList,
            contentTypes,
            content,
            (result) => {
                completed++;
                if (result.status === 'created') created++;
                else if (result.status === 'updated') updated++;
                else if (result.status === 'skipped') skipped++;
                else if (result.status === 'error') errors++;

                setSyncProgress(prev => [...prev.slice(-7), {
                    targetPath: result.targetPath || result.source.relativePath,
                    status: result.status === 'error' ? 'error' : 'success',
                    error: result.error
                }]);
                setSyncStats({ total: totalFiles, completed, created, updated, skipped, errors });
            }
        );

        setViewState('done');
    };

    // Render loading
    if (loading) {
        return (
            <Box padding={1}>
                <Text color="cyan"><Spinner type="dots" /> Đang tải components...</Text>
            </Box>
        );
    }

    // Render error
    if (error) {
        return (
            <Box flexDirection="column" padding={1}>
                <Text color="red">❌ Error: {error}</Text>
                <Text dimColor>Press q to exit</Text>
            </Box>
        );
    }

    if (viewState === 'previewing') {
        return (
            <Box padding={1}>
                <Text color="cyan"><Spinner type="dots" /> Resolving dependencies and local state...</Text>
            </Box>
        );
    }

    if (viewState === 'preview') {
        const visiblePreviewItems = previewItems.slice(0, 12);

        return (
            <Box flexDirection="column" padding={1}>
                <Box marginBottom={1}>
                    <Text bold color="cyan">Review Apply Plan</Text>
                    <Text color="green"> [{selectedPaths.size} selected]</Text>
                </Box>

                {previewError ? (
                    <Box flexDirection="column" borderStyle="round" borderColor="red" padding={1}>
                        <Text color="red">Preview failed: {previewError}</Text>
                        <Text dimColor>Esc Back · Ctrl+Q Exit</Text>
                    </Box>
                ) : (
                    <>
                        <Box marginBottom={1}>
                            <Text>
                                <Text color="green">{previewStats.add} add</Text>
                                {' · '}<Text color="cyan">{previewStats.update} update</Text>
                                {' · '}<Text color="yellow">{previewStats.skip} skip</Text>
                                {previewStats.blocked > 0 && <>{' · '}<Text color="red">{previewStats.blocked} blocked</Text></>}
                                {previewStats.dependencies > 0 && <>{' · '}<Text color="magenta">{previewStats.dependencies} deps</Text></>}
                            </Text>
                        </Box>

                        <Box flexDirection="column" borderStyle="round" borderColor={previewStats.blocked > 0 ? 'yellow' : 'cyan'} padding={1}>
                            {visiblePreviewItems.map((item) => (
                                <Box key={item.filepath}>
                                    <Text color={previewStatusColor(item.status)}>
                                        {previewStatusLabel(item.status)}
                                    </Text>
                                    <Text> {truncate(item.filepath, 58)}</Text>
                                    {item.isDependency && <Text color="magenta"> dep</Text>}
                                    <Text dimColor> {item.reason}</Text>
                                </Box>
                            ))}
                            {previewItems.length > visiblePreviewItems.length && (
                                <Text dimColor>  ... +{previewItems.length - visiblePreviewItems.length} more</Text>
                            )}
                        </Box>

                        {previewStats.blocked > 0 && !force && (
                            <Box marginTop={1}>
                                <Text color="yellow">Some local files are modified. Re-run with --force to overwrite them.</Text>
                            </Box>
                        )}
                    </>
                )}

                <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={1}>
                    <Text dimColor>[Enter] Apply · [S] Apply + sync · [Esc] Back · [Ctrl+Q] Exit</Text>
                </Box>
            </Box>
        );
    }

    // Render installing
    if (viewState === 'installing') {
        return (
            <Box flexDirection="column" padding={1}>
                <Box marginBottom={1}>
                    <Text bold color="cyan">📦 Installing Components</Text>
                </Box>

                <Box marginBottom={1}>
                    <ProgressBar current={installStats.completed} total={installStats.total} width={50} />
                </Box>

                <Box flexDirection="column" borderStyle="round" borderColor="gray" padding={1} height={10}>
                    {installProgress.slice(-8).map((item) => (
                        <Box key={item.filepath}>
                            <StatusIcon status={
                                item.status === 'success' ? 'success' :
                                    item.status === 'error' ? 'error' :
                                        item.status === 'downloading' ? 'loading' : 'pending'
                            } />
                            <Text> {item.filepath}</Text>
                            {item.error && <Text color="red" dimColor> ({item.error})</Text>}
                        </Box>
                    ))}
                </Box>

                <Box marginTop={1}>
                    <Text>
                        📊 <Text color="green">{installStats.added} added</Text>
                        {' · '}<Text color="cyan">{installStats.updated} updated</Text>
                        {installStats.skipped > 0 && <>{' · '}<Text color="yellow">{installStats.skipped} up-to-date</Text></>}
                        {' · '}<Text color="red">{installStats.failed} failed</Text>
                    </Text>
                </Box>
            </Box>
        );
    }

    // Render summary (after install, before IDE sync)
    if (viewState === 'summary') {
        return (
            <Box flexDirection="column" padding={1}>
                <Box marginBottom={1}>
                    <Text bold color="green">✅ Installation Complete!</Text>
                </Box>

                <Box flexDirection="column" borderStyle="round" borderColor="gray" padding={1}>
                    <Text>{installStats.total} components processed:</Text>
                    <Text>  └─ <Text color="green">{installStats.added}</Text> newly added</Text>
                    <Text>  └─ <Text color="cyan">{installStats.updated}</Text> updated</Text>
                    {installStats.skipped > 0 && <Text>  └─ <Text color="yellow">{installStats.skipped}</Text> up-to-date (skipped)</Text>}
                    {installStats.failed > 0 && <Text>  └─ <Text color="red">{installStats.failed}</Text> failed</Text>}
                </Box>

                <Box marginTop={1}>
                    <Text>📁 Location: <Text color="cyan">{process.cwd()}/.jai1</Text></Text>
                </Box>

                <Box marginTop={1} flexDirection="column">
                    <Text bold color="yellow">📦 Next Step: Sync to IDE(s)?</Text>
                    <Text dimColor>Sync .jai1/ content to your IDE directories (rules, workflows)</Text>
                </Box>

                <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={1}>
                    <Text dimColor>
                        [Enter] Select IDE(s) to sync · [S/Ctrl+Q] Skip and exit
                    </Text>
                </Box>
            </Box>
        );
    }

    // Render IDE sync selection
    if (viewState === 'ide-sync') {
        return (
            <Box flexDirection="column" padding={1}>
                <Box marginBottom={1}>
                    <Text bold color="cyan">🔄 Select IDE(s) to Sync</Text>
                </Box>

                <Box marginBottom={1}>
                    <Text dimColor>Sync .jai1/ content (rules, workflows) to IDE-specific directories</Text>
                </Box>

                <Box flexDirection="column" borderStyle="round" borderColor="cyan" padding={1}>
                    {availableIdes.map((ide, i) => {
                        const ideConfig = IDE_MIGRATION_CONFIGS[ide];
                        const isCursor = i === ideCursorIndex;
                        const isChecked = selectedIdes.has(ide);

                        return (
                            <Box key={ide}>
                                <Text color={isCursor ? 'cyan' : 'white'}>
                                    {isCursor ? '❯ ' : '  '}
                                    {isChecked ? '[✓]' : '[ ]'} {ideConfig.icon} {ideConfig.name}
                                </Text>
                            </Box>
                        );
                    })}
                </Box>

                {selectedIdes.size > 0 && (
                    <Box marginTop={1}>
                        <Text>Selected: <Text color="green">{selectedIdes.size}</Text> IDE(s)</Text>
                    </Box>
                )}

                <Box marginTop={1} borderStyle="single" borderColor="gray" paddingX={1}>
                    <Text dimColor>
                        [↑↓] Navigate · [␣] Toggle · [A] Select all · [C] Clear · [Enter] Sync · [S/Ctrl+Q] Skip
                    </Text>
                </Box>
            </Box>
        );
    }

    // Render syncing
    if (viewState === 'syncing') {
        return (
            <Box flexDirection="column" padding={1}>
                <Box marginBottom={1}>
                    <Text bold color="cyan">🔄 Syncing to IDE(s)</Text>
                </Box>

                <Box marginBottom={1}>
                    <ProgressBar current={syncStats.completed} total={syncStats.total} width={50} />
                </Box>

                <Box flexDirection="column" borderStyle="round" borderColor="gray" padding={1} height={10}>
                    {syncProgress.slice(-8).map((item, idx) => (
                        <Box key={`${item.targetPath}-${idx}`}>
                            <StatusIcon status={
                                item.status === 'success' ? 'success' :
                                    item.status === 'error' ? 'error' :
                                        item.status === 'syncing' ? 'loading' : 'pending'
                            } />
                            <Text> {item.targetPath}</Text>
                            {item.error && <Text color="red" dimColor> ({item.error})</Text>}
                        </Box>
                    ))}
                </Box>

                <Box marginTop={1}>
                    <Text>
                        📊 <Text color="green">{syncStats.created} created</Text>
                        {' · '}<Text color="cyan">{syncStats.updated} updated</Text>
                        {syncStats.skipped > 0 && <>{' · '}<Text color="yellow">{syncStats.skipped} skipped</Text></>}
                        {syncStats.errors > 0 && <>{' · '}<Text color="red">{syncStats.errors} errors</Text></>}
                    </Text>
                </Box>
            </Box>
        );
    }

    // Render done (after IDE sync)
    if (viewState === 'done') {
        return (
            <Box flexDirection="column" padding={1}>
                <Box marginBottom={1}>
                    <Text bold color="green">🎉 All Done!</Text>
                </Box>

                <Box flexDirection="column" borderStyle="round" borderColor="gray" padding={1}>
                    <Text bold>📦 Components Applied:</Text>
                    <Text>
                        {'  └─ '}<Text color="green">{installStats.added}</Text> added, <Text color="cyan">{installStats.updated}</Text> updated
                        {installStats.skipped > 0 && <>{', '}<Text color="yellow">{installStats.skipped}</Text> up-to-date</>}
                    </Text>

                    {syncStats.total > 0 && (
                        <>
                            <Text> </Text>
                            <Text bold>🔄 IDE Sync:</Text>
                            <Text>  └─ <Text color="green">{syncStats.created}</Text> created, <Text color="cyan">{syncStats.updated}</Text> updated</Text>
                            <Text>  └─ Synced to: {Array.from(selectedIdes).map(ide => IDE_MIGRATION_CONFIGS[ide].name).join(', ')}</Text>
                        </>
                    )}
                </Box>

                <Box marginTop={1}>
                    <Text>📁 Framework: <Text color="cyan">{process.cwd()}/.jai1</Text></Text>
                </Box>

                <Box marginTop={1}>
                    <Text dimColor>Press Enter or Q to exit</Text>
                </Box>
            </Box>
        );
    }

    // Render browse (main view) - searchable catalog
    const packageWindow = getVisibleWindow(packageOptions.length, selectedPackageIndex, PACKAGE_WINDOW_SIZE);
    const visiblePackages = packageOptions.slice(packageWindow.start, packageWindow.end);
    const packageRangeLabel = packageOptions.length === 0
        ? '0/0'
        : `${packageWindow.start + 1}-${packageWindow.end}/${packageOptions.length}`;
    const resultWindow = getVisibleWindow(searchResults.length, cursorIndex, RESULT_WINDOW_SIZE);
    const visibleResults = searchResults.slice(resultWindow.start, resultWindow.end);
    const activeResult = searchResults[cursorIndex];
    const selectedList = Array.from(selectedPaths);
    const rangeLabel = searchResults.length === 0
        ? '0/0'
        : `${resultWindow.start + 1}-${resultWindow.end}/${searchResults.length}`;

    return (
        <Box flexDirection="column" paddingX={1}>
            <Box>
                <Text bold color="cyan">📦 Jai1 Apply</Text>
                {selectedPaths.size > 0 && (
                    <Text color="green"> [{selectedPaths.size} selected]</Text>
                )}
            </Box>

            {recommendedPackages.length > 0 && (
                <Box>
                    <Text bold color="yellow">Recommended </Text>
                    {recommendedPackages.map((pkg, index) => (
                        <Text key={pkg.tag}>
                            [{index + 1}] {pkg.tag}{index < recommendedPackages.length - 1 ? '  ' : ''}
                        </Text>
                    ))}
                    {!isSearchFocused && <Text dimColor>  press number to add package</Text>}
                </Box>
            )}

            <Box>
                <Box
                    flexDirection="column"
                    borderStyle="round"
                    borderColor={activePane === 'packages' && !isSearchFocused ? 'cyan' : 'gray'}
                    width={28}
                    padding={1}
                >
                    <Box>
                        <Text bold dimColor>PACKAGES </Text>
                        <Text dimColor>({packageRangeLabel})</Text>
                    </Box>
                    <Box flexDirection="column" marginTop={1}>
                        {visiblePackages.map((pkg, i) => {
                            const absoluteIndex = packageWindow.start + i;
                            return (
                                <PackageRow
                                    key={pkg.tag}
                                    pkg={pkg}
                                    active={absoluteIndex === selectedPackageIndex}
                                    focused={activePane === 'packages' && !isSearchFocused}
                                />
                            );
                        })}
                    </Box>
                </Box>

                <Box flexDirection="column" flexGrow={1} marginLeft={1}>
                    <Box
                        flexDirection="column"
                        borderStyle="round"
                        borderColor={activePane === 'components' ? 'cyan' : 'gray'}
                        padding={1}
                    >
                        <Box>
                            <Text color={isSearchFocused ? 'cyan' : 'gray'}>Search </Text>
                            <TextInput
                                value={searchQuery}
                                onChange={setSearchQuery}
                                placeholder="Search packages, skills, workflows..."
                                focus={isSearchFocused}
                            />
                        </Box>

                        <Box>
                            <Text bold>RESULTS </Text>
                            {selectedPackage && selectedPackage.tag !== 'All' && <Text color="cyan">pkg:{selectedPackage.tag} </Text>}
                            <Text dimColor>({rangeLabel})</Text>
                        </Box>

                        <Box flexDirection="column">
                            {visibleResults.map((result, i) => {
                                const absoluteIndex = resultWindow.start + i;
                                return (
                                    <ResultRow
                                        key={result.component.filepath}
                                        result={result}
                                        active={absoluteIndex === cursorIndex && activePane === 'components' && !isSearchFocused}
                                        checked={selectedPaths.has(result.component.filepath)}
                                        installed={installed[result.component.filepath]}
                                    />
                                );
                            })}

                            {searchResults.length === 0 && (
                                <Box flexDirection="column">
                                    <Text color="yellow">No matching components.</Text>
                                    <Text dimColor>{getNoResultsSuggestion(searchQuery)}</Text>
                                </Box>
                            )}
                        </Box>
                    </Box>

                    <Box marginTop={1} flexDirection="column">
                        <Text bold dimColor>PREVIEW </Text>
                        {activeResult ? (
                            <>
                                <Text color="cyan">{truncate(activeResult.component.name || activeResult.component.filepath, 68)}</Text>
                                {activeResult.component.description && (
                                    <Text dimColor>{truncate(activeResult.component.description, 68)}</Text>
                                )}
                                <Text dimColor>
                                    {truncate(activeResult.component.filepath, 34)} · {activeResult.type}
                                    {activeResult.packageTags.length > 0 ? ` · ${activeResult.packageTags.join(', ')}` : ''}
                                    {activeResult.targetLabel ? ` · target:${activeResult.targetLabel}` : ''}
                                </Text>
                            </>
                        ) : (
                            <Text dimColor>Select a result to see details.</Text>
                        )}
                    </Box>
                </Box>
            </Box>

            {selectedPaths.size > 0 && (
                <Box>
                    <Text color="green">Selected {selectedPaths.size}: </Text>
                    <Text dimColor>
                        {selectedList.slice(0, 3).map((fp) => truncate(fp, 24)).join(', ')}
                        {selectedPaths.size > 3 ? ` +${selectedPaths.size - 3} more` : ''}
                    </Text>
                </Box>
            )}

            <Box borderStyle="single" borderColor="gray" paddingX={1}>
                <Text dimColor>
                    {isSearchFocused && 'Type to search · [Enter/↓] Results · [Tab] Packages'}
                    {!isSearchFocused && activePane === 'packages' && '[↑↓] Package · [→/Tab] Results · [␣] Select shown'}
                    {!isSearchFocused && activePane === 'components' && '[↑↓] Navigate · [␣] Toggle · [/] Search'}
                    {!isSearchFocused && ' · [A] All · [C] Clear · [Enter] Preview'}
                </Text>
            </Box>
        </Box>
    );
};

const PackageRow: React.FC<{ pkg: CatalogPackageOption; active: boolean; focused: boolean }> = ({ pkg, active, focused }) => {
    const marker = active ? (focused ? '❯ ' : '› ') : '  ';
    const color = active && focused ? 'cyan' : active ? 'white' : 'gray';
    const badge = pkg.kind === 'primary' ? 'primary' : pkg.kind === 'addon' ? 'addon' : 'all';

    return (
        <Box>
            <Text color={color}>
                {marker}{truncate(pkg.tag, 13)} <Text dimColor>({pkg.count})</Text>
            </Text>
            <Text dimColor> {badge}</Text>
            {pkg.targetLabel && <Text color="magenta"> {pkg.targetLabel}</Text>}
        </Box>
    );
};

const ResultRow: React.FC<{
    result: CatalogSearchResult;
    active: boolean;
    checked: boolean;
    installed?: InstalledComponent;
}> = ({ result, active, checked, installed }) => {
    const marker = active ? '❯ ' : '  ';
    const installedLabel = installed?.modified ? 'local' : installed ? 'installed' : 'new';
    const installedColor = installed?.modified ? 'yellow' : installed ? 'green' : 'gray';

    return (
        <Box>
            <Text color={active ? 'cyan' : 'white'}>
                {marker}{checked ? '[✓]' : '[ ]'} {truncate(result.component.filepath, 46)}
            </Text>
            <Text dimColor> {result.type}</Text>
            <Text color={installedColor}> {installedLabel}</Text>
            {result.targetLabel && <Text color="magenta"> target:{result.targetLabel}</Text>}
        </Box>
    );
};

function previewStatusLabel(status: PreviewItem['status']): string {
    if (status === 'add') return '+';
    if (status === 'update') return '~';
    if (status === 'skip') return '=';
    return '!';
}

function previewStatusColor(status: PreviewItem['status']): string {
    if (status === 'add') return 'green';
    if (status === 'update') return 'cyan';
    if (status === 'skip') return 'yellow';
    return 'red';
}

function truncate(value: string, maxLength: number): string {
    if (value.length <= maxLength) return value;
    if (maxLength <= 3) return value.slice(0, maxLength);
    return `${value.slice(0, maxLength - 3)}...`;
}
